import React, {useEffect} from 'react';
import {curry} from 'ramda';
import wrap from 'wraptile';
import {useSelector} from 'react-redux';
import {useForm, useField} from 'react-final-form-hooks';
import {selectRatings} from '@pluto/rating-select/Redux/ratingSelectors';
import {RatingSelect} from '@pluto/rating-select';
import {ReactSelectOpt} from '@pluto/select';
import {Checkbox} from '@material-ui/core';
import {OptionsType} from '@pluto/mrss-defaults/MrssDefaultsTypes';
import AutoComplete from '@pluto/autocomplete/AutocompleteContainer';
import {getFilmLabels} from '@pluto/get-film-labels';
import ErrorIcon from '@material-ui/icons/Error';
import {
    Descriptor,
    MetadataTerritorialLanguage,
    FormFieldType,
    TerritoriaFormInitialValues,
} from './ContentItemViewMetadataTypes';
import {getTextWithEmptyFieldError} from './ContentItemViewMetadataUtils';
import {TerritorialMetadataType} from './ContentItemViewMetadataTerritorial';
import {TargetAgeCountryItem} from '@pluto/target-age/TargetAgeTypes';
import {TargetAgeSelect} from '../../@pluto/target-age';

const onSubmit = () => {};

export type SetTerritorialType = (val: TerritorialMetadataType) => void;
export interface MetadataTerritorialLanguageProps {
    rating: string;
    isEditMode: boolean;
    countryCode: string;
    captionRequired: string;
    targetAge: TargetAgeCountryItem;
    ratingDescriptors: string[];
    setTerritorialMetadata: SetTerritorialType;
    enableRatingDescriptors: Descriptor[];
    setValidTabs: (val: boolean) => void;
    cancelEditMode: boolean;
}

export type ChangeEvent = {
    target: {
        checked: boolean;
    };
};

export const handleRatingChange = curry(
    (
        setTerritorialMetadata: SetTerritorialType,
        countryCode: string,
        onChange: (val: string) => void,
        {target: {value}}: any
    ) => {
        const metadata = {
            countryCode,
            rating: value,
        };
        setTerritorialMetadata(getMetadata(countryCode, metadata) as any);
        onChange(value);
    }
);

export const mapDescriptorValues = (descriptor: Descriptor) => descriptor.code;
export const filterFieldValues = curry(
    (countryCodeDescriptorValues: string[], value: string) =>
        !countryCodeDescriptorValues.includes(value)
);
export const filterDescriptors = curry(
    (countryCode: string, descriptor: Descriptor) =>
        descriptor.countryCode === countryCode
);

export const handleTargetAgeChange = curry((setTerritorialMetadata: SetTerritorialType, countryCode: string, onChange: (val: string) => void, {target: {value}}: any) => {
    const metadata = {
        countryCode,
        targetAgeId: value,
    };

    setTerritorialMetadata(getMetadata(countryCode, metadata) as any);
    onChange(value);
});

export const getTargetAge = (
    onChange: (val: string) => void,
    value: string,
    targetAge: TargetAgeCountryItem,
    isEditMode: boolean,
    requiredFields: string[],
    countryCode: string,
    setTerritorialMetadata: SetTerritorialType
) => {
    if (isEditMode) {
        return (
            <TargetAgeSelect
                value={value}
                onChange={handleTargetAgeChange(setTerritorialMetadata, countryCode, onChange)}
                countryCode={countryCode}
            />
        );
    }

    if (!targetAge) {
        return getTextWithEmptyFieldError(requiredFields, targetAge, 'targetAge');
    }

    return targetAge.label;
}

export const getRating = (
    ratings: {[key: string]: string},
    onChange: (val: string) => void,
    value: string,
    rating: string,
    isEditMode: boolean,
    requiredFields: string[],
    countryCode: string,
    setTerritorialMetadata: SetTerritorialType
) => {
    const ratingsValues = Object.keys(ratings);

    if (isEditMode) {
        return (
            <RatingSelect
                value={value}
                onChange={handleRatingChange(setTerritorialMetadata, countryCode, onChange)}
            />
        );
    }

    if (!rating) {
        return getTextWithEmptyFieldError(requiredFields, rating, 'rating');
    }

    if (!ratingsValues.includes(rating)) {
        return (
            <div className="metadata-item">
                <div className="metadata-item__value">
                    {rating}
                </div>
                <div className="metadata-item__value-error">
                    <div className="metadata-item__value-error_icon">
                        <ErrorIcon />
                    </div>
                    <div>
                        Please select valid option
                    </div>
                </div>
            </div>
        );
    }

    return rating;
};

export const getMetadata = curry(
    (
        countryCode: string,
        metadata: any,
        territorialMetadata: TerritorialMetadataType
    ) => ({
        ...territorialMetadata,
        [countryCode]: {
            ...territorialMetadata[countryCode],
            ...metadata,
        },
    })
);

export const handleCheckboxChange = curry(
    (
        countryCode: string,
        setTerritorialMetadata: SetTerritorialType,
        onChange: (val: string) => void,
        {target: {checked}}: ChangeEvent
    ) => {
        const metadata = {
            countryCode,
            captionsRequired: checked,
        };

        setTerritorialMetadata(getMetadata(countryCode, metadata) as any);
        onChange(checked ? 'Yes' : 'No');
    }
);

export const getCaptionRequired = (
    onChange: (val: string) => void,
    value: string,
    captionRequired: string,
    isEditMode: boolean,
    setTerritorialMetadata: SetTerritorialType,
    countryCode: string
) => {
    const checked = value.toLowerCase() === 'yes';

    return isEditMode ? (
        <Checkbox
            checked={checked}
            onChange={handleCheckboxChange(countryCode, setTerritorialMetadata, onChange)}
        />
    ) : (
        captionRequired
    );
};

export const mapDescriptors = (label: string) => ({
    label,
    value: label,
});
export const mapDescriptorOptions = ({label, code}: Descriptor) => ({
    label,
    value: code,
});

export const getDescriptorLabels = (ratingDescriptors: string[], className: string) => {
    const withLabels = ratingDescriptors.map(mapDescriptors);
    return getFilmLabels(withLabels, className);
};

export const mapValues = (val: OptionsType) => val.value || val;

export const filterChosenValues = (values: string[]) => {
    if (!values.length) {
        return [];
    }
    const validValues: string[] = values.map(mapValues as any);
    // find and remove from array selected value
    const [chosenValue] = validValues.splice(validValues.length - 1, 1);

    if (!validValues.includes(chosenValue)) {
        return [...validValues, chosenValue];
    }

    validValues.splice(validValues.indexOf(chosenValue), 1);

    return validValues;
};

export const handleDescriptorChange = curry(
    (
        countryCode: string,
        setTerritorialMetadata: SetTerritorialType,
        onChange: (val: string[]) => void,
        event: React.ChangeEvent<any>,
        values: string[]
    ) => {
        const newValues = filterChosenValues(values);

        const metadata = {
            countryCode,
            ratingDescriptors: newValues,
        };

        setTerritorialMetadata(getMetadata(countryCode, metadata) as any);
        onChange(newValues);
    }
);

export const checkEveryElement = curry((descriptorValues: string[], value: string) =>
    descriptorValues.includes(value));

export const getDescriptorOptions = (
    descriptorsByCountryCode: Descriptor[],
    fieldValue: string[],
    existAllElements: boolean
) => {
    if (!existAllElements) {
        const countryCodeDescriptorValues = descriptorsByCountryCode.map(
            mapDescriptorValues
        );
        const notValidValues = fieldValue.filter(
            filterFieldValues(countryCodeDescriptorValues)
        );
        return [
            ...descriptorsByCountryCode.map(mapDescriptorOptions as any),
            ...notValidValues.map(mapDescriptors),
        ];
    }
    return descriptorsByCountryCode.map(mapDescriptorOptions as any);
};

export const filterValidDescriptors = curry(
    (ratingDescriptors: string[], {value}: OptionsType) =>
        ratingDescriptors.includes(value)
);
export const filterNotValidDescriptors = curry(
    (filteredDescriptorLabels: string[], label: string) =>
        !filteredDescriptorLabels.includes(label)
);
export const mapLabels = ({label}: OptionsType) => label;

export const getDescriptorsByCountryCode = (
    descriptors: Descriptor[],
    countryCode: string
) => descriptors.filter(filterDescriptors(countryCode));

export const matchElements = (notValidValues: string[], selectedElements: any) => {
    for (const element of selectedElements) {
        const errorClassList = 'error-label__border-label';
        const text = element.textContent;
        const parent = element.parentElement;

        if (notValidValues.includes(text) && parent) {
            parent.classList.add(errorClassList);
        }

        if (parent.classList.contains(errorClassList) && !notValidValues.includes(text)) {
            parent.classList.remove(errorClassList);
        }
    }
};

export const setClassNames = wrap((notValidValues: string[]) => {
    const selectedElements = document.querySelectorAll('.MuiChip-label');
    matchElements(notValidValues, selectedElements);
});

export const setClassNameForNotValidValues = (notValidValues: string[]) => {
    // wait till <AutoComplete/> renders elements
    setTimeout(setClassNames(notValidValues), 10);
};

export const getRatingDescriptors = (
    values: string[],
    onChange: (val: string[]) => void,
    ratingDescriptors: string[],
    isEditMode: boolean,
    countryCode: string,
    enableRatingDescriptors: Descriptor[],
    descriptorsByCountryCode: Descriptor[],
    setTerritorialMetadata: SetTerritorialType
) => {
    const filteredDescriptorLabels = descriptorsByCountryCode.map(mapDescriptorValues);
    const isValidDescriptors = ratingDescriptors.every(
        checkEveryElement(filteredDescriptorLabels)
    );
    const existAllOptions = values.every(checkEveryElement(filteredDescriptorLabels));
    const notValidValues = ratingDescriptors.filter(
        filterNotValidDescriptors(filteredDescriptorLabels)
    );
    const filteredDescriptorOptions = descriptorsByCountryCode.map(mapDescriptorOptions);
    const validValues = filteredDescriptorOptions
        .filter(filterValidDescriptors(ratingDescriptors))
        .map(mapLabels);

    if (isEditMode) {
        setClassNameForNotValidValues(notValidValues);
        const options = getDescriptorOptions(
            descriptorsByCountryCode,
            ratingDescriptors,
            existAllOptions
        );
        return (
            <AutoComplete
                placeholder=""
                values={values}
                onChange={
                    handleDescriptorChange(
                        countryCode,
                        setTerritorialMetadata,
                        onChange
                    ) as any
                }
                options={options as ReactSelectOpt[]}
                isLoading={false}
                limitTags={100}
                variant="filled"
            />
        );
    }

    if (!isValidDescriptors) {
        const validLabels = getDescriptorLabels(validValues, 'keywords-label');
        const notValidLabels = getDescriptorLabels(
            notValidValues,
            'keywords-label error-label'
        );

        return validLabels?.concat((notValidLabels as unknown) as JSX.Element[]);
    }

    return getDescriptorLabels(validValues, 'keywords-label');
};

export const getValidField = curry(
    (descriptorsByCountryCode: Descriptor[], values: string[]) => {
        const filteredDescriptorLabels = descriptorsByCountryCode.map(
            mapDescriptorValues
        );
        const isValid = values.every(checkEveryElement(filteredDescriptorLabels));
        return isValid ? '' : 'Invalid options, select new';
    }
);

export const setValidTab = wrap((setValidTab: (val: boolean) => void, valid: boolean) =>
    setValidTab(valid));

export const setDefaultValues = wrap(
    (
        cancelEditMode: boolean,
        ratingField: FormFieldType,
        captionRequiredField: FormFieldType,
        ratingDescriptorsField: FormFieldType,
        initialValues: MetadataTerritorialLanguage
    ) => {
        if (cancelEditMode) {
            const {rating, captionRequired, ratingDescriptors} = initialValues;
            ratingField.input.onChange(rating);
            captionRequiredField.input.onChange(captionRequired);
            ratingDescriptorsField.input.onChange(ratingDescriptors);
        }
    }
);

export const resetForm = wrap((form: any, initialValues: TerritoriaFormInitialValues) =>
    form.reset(initialValues));

export default function ContentItemViewMetadataTerritorialTab(
    props: MetadataTerritorialLanguageProps
) {
    const {
        rating,
        isEditMode,
        targetAge,
        captionRequired,
        ratingDescriptors,
        countryCode,
        setTerritorialMetadata,
        enableRatingDescriptors,
        setValidTabs,
        // cancelEditMode,
    } = props;

    let requiredFields = ['rating'];

    if (countryCode === 'us') {
        requiredFields = ['rating', 'targetAge'];
    }

    const initialValues = {
        rating,
        captionRequired,
        ratingDescriptors,
        targetAge: targetAge?.id
    };
    const ratings = useSelector(selectRatings);

    const descriptorsByCountryCode = getDescriptorsByCountryCode(
        enableRatingDescriptors,
        countryCode
    );
    const {form, valid} = useForm({
        onSubmit,
        initialValues,
    });

    useEffect(setValidTab(setValidTabs, valid), [valid]);
    useEffect(resetForm(form, initialValues), [isEditMode]);

    const ratingField = useField('rating', form);
    const captionRequiredField = useField('captionRequired', form);
    const ratingDescriptorsField = useField(
        'ratingDescriptors',
        form,
        getValidField(descriptorsByCountryCode) as any
    );
    const targetAgeField = useField('targetAge', form);

    // useEffect(
    //     setDefaultValues(
    //         cancelEditMode,
    //         ratingField,
    //         captionRequiredField,
    //         ratingDescriptorsField,
    //         initialValues
    //     ),
    //     [cancelEditMode]
    // );

    return (
        <div className="qc-metadata__territorial-tab-panel">
            <div className="qc-metadata__rating-caption-wrapper">
                <div className="caption-wrapper">
                    <div className="caption-label">Captions Required</div>
                    <div className="caption-value">
                        {getCaptionRequired(
                            captionRequiredField.input.onChange,
                            captionRequiredField.input.value,
                            captionRequired,
                            isEditMode,
                            setTerritorialMetadata,
                            countryCode
                        )}
                    </div>
                </div>
                <div className="rating-wrapper">
                    <div className="rating-label">Rating*</div>
                    <div className="rating-value">
                        {getRating(
                            ratings as any,
                            ratingField.input.onChange,
                            ratingField.input.value,
                            rating,
                            isEditMode,
                            requiredFields,
                            countryCode,
                            setTerritorialMetadata
                        )}
                    </div>
                </div>
            </div>
            {
                countryCode === 'us' ?
                    <div className="target-age-wrapper">
                        <div className="target-age-label">
                            Target age*
                        </div>
                        <div className="target-age-value">
                            {getTargetAge(
                                targetAgeField.input.onChange,
                                targetAgeField.input.value,
                                targetAge,
                                isEditMode,
                                requiredFields,
                                countryCode,
                                setTerritorialMetadata
                            )}
                        </div>
                    </div> : null
            }
            <div className="rating-reason">
                <div className="rating-reason-label">Rating Descriptors</div>
                {getRatingDescriptors(
                    ratingDescriptorsField.input.value || [],
                    ratingDescriptorsField.input.onChange,
                    ratingDescriptors,
                    isEditMode,
                    countryCode,
                    enableRatingDescriptors,
                    descriptorsByCountryCode,
                    setTerritorialMetadata
                )}
                {isEditMode && !valid && (
                    <div className="metadata-item__value-error">
                        <div className="metadata-item__value-error_icon">
                            <ErrorIcon />
                        </div>
                        <div>Invalid rating descriptors, remove or replace</div>
                    </div>
                )}
            </div>
        </div>
    );
}
